import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { updateUser } from 'api/users'
import FormCheckbox from 'components/form-elements/FormCheckbox'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, PHONE_IS_INVALID } from 'const'
import { CohortsMultiSelect } from 'features/Cohorts'
import { useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { OrganizationalUser, UserTypeLabels, UserUpdateRequest } from 'types'
import { boolean, literal, number, object, string, TypeOf, ZodIssueCode } from 'zod'
import { UserAdminWelcomeButtons } from '../UserAdminWelcomeButtons'

const formSchema = object({
  firstname: string().min(1, FIELD_IS_REQUIRED),
  lastname: string().min(1, FIELD_IS_REQUIRED),
  email: string().email(EMAIL_IS_INVALID),
  cohorts: object({
    label: string(),
    value: number(),
  })
    .array()
    .optional(),
  phoneNumber: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
  isEmailNotificationEnabled: boolean(),
  isSmsNotificationEnabled: boolean(),
}).superRefine((data, refinementContext) => {
  if (data.isSmsNotificationEnabled && (!data.phoneNumber || data.phoneNumber.length < 10)) {
    refinementContext.addIssue({
      path: ['phoneNumber'],
      code: ZodIssueCode.custom,
      message: FIELD_IS_REQUIRED,
    })
  }
})

type FormType = TypeOf<typeof formSchema>

export function UserOrgDetailsForm({
  user,
  isAdminPage,
  onAfterUpdate,
  /** New-user flow only: default email opt-in when the API has not set a value yet. Edit mode always mirrors the backend. */
  isCreate = false,
}: {
  user: OrganizationalUser
  isAdminPage: boolean
  onAfterUpdate: () => void
  isCreate?: boolean
}) {
  const [previousEmail] = useState(user.email)

  const defaultCohorts = useMemo(() => {
    if (!user.cohorts) return []

    return user.cohorts.map((cohort) => ({ label: cohort.name, value: cohort.id }))
  }, [user])

  const defaultValues = useMemo(() => {
    const emailFromApi = Boolean(user.isEmailNotificationEnabled)
    const smsFromApi = Boolean(user.isSmsNotificationEnabled)

    return {
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      organizationName: user.organizationName,
      role: UserTypeLabels[user.type[0]],
      ownedAccessCodes: user.ownedAccessCodes.map((accessCode) => accessCode.code).join(', '),
      cohorts: defaultCohorts,
      phoneNumber: user.phoneNumber,
      isEmailNotificationEnabled: isCreate ? user.isEmailNotificationEnabled ?? true : emailFromApi,
      isSmsNotificationEnabled: isCreate ? user.isSmsNotificationEnabled ?? false : smsFromApi,
    }
  }, [user, defaultCohorts, isCreate])

  const methods = useForm<FormType>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const { formState, watch } = methods
  const emailValue = watch('email')
  const isEmailDirty = emailValue !== user.email
  const phoneNumber = watch('phoneNumber')

  const smsWarningText = phoneNumber
    ? 'Agreement has been given to receive sms. User is informed that they can opt out by sending STOP'
    : 'Please enter mobile phone number.\n Agreement has been given to receive sms. User is informed that they can opt out by sending STOP'

  const saveUserMutation = useMutation(updateUser)

  const getRequestData = useCallback(() => {
    const values = methods.getValues()

    const data: UserUpdateRequest = {
      id: user.id,
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      phoneNumber: values.phoneNumber,
      isEmailNotificationEnabled: values.isEmailNotificationEnabled,
      isSmsNotificationEnabled: values.isSmsNotificationEnabled,
    }

    if (isAdminPage) {
      data.cohortIds = values.cohorts?.map((item) => item.value) || []
    }

    return data
  }, [methods, user, isAdminPage])

  const handleCheckUpdate = useCallback(async () => {
    const request = getRequestData()

    await saveUserMutation.mutateAsync(request)

    // Show notification if email changed and user is admin
    if (isAdminPage && emailValue !== previousEmail) {
      NotificationSys.showWarning(
        `Please note, the previous user Email (${previousEmail}) will be removed from the system.`,
      )
    }

    onAfterUpdate()
  }, [getRequestData, onAfterUpdate, saveUserMutation, isAdminPage, previousEmail, emailValue])

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(handleCheckUpdate)}
        noValidate
        width="100%"
        mt={4}
      >
        <Grid container spacing={4}>
          <Grid item sm={12} md={12} lg={6} xl={6}>
            <Stack spacing={2}>
              <FormInput label="First Name" name="firstname" required fullWidth />
              <FormInput label="Last Name" name="lastname" required fullWidth />
              <FormInput label="Email" name="email" required fullWidth autoComplete="off" />

              {isAdminPage && <CohortsMultiSelect defaultValue={defaultValues.cohorts} />}

              <FormInput label="Organization" name="organizationName" disabled fullWidth />
              <FormInput label="Role" name="role" disabled fullWidth />
              <FormInput label="Access Codes" name="ownedAccessCodes" disabled fullWidth />
              <FormPhone label="Mobile phone number" name="phoneNumber" fullWidth />

              <Stack mt={2}>
                <Typography fontWeight={600}>Notification Settings</Typography>
                <Typography fontSize={14} sx={{ opacity: '0.6' }}>
                  Receive reminders Weekly
                </Typography>
              </Stack>

              <Stack flexDirection="row">
                <FormCheckbox name="isEmailNotificationEnabled" label="Email" />
                <FormCheckbox
                  name="isSmsNotificationEnabled"
                  label="Text messages"
                  warningText={smsWarningText}
                />
              </Stack>

              {isAdminPage && (
                <UserAdminWelcomeButtons
                  userId={user.id}
                  isEmailDirty={isEmailDirty}
                  isFormSubmitting={formState.isSubmitting}
                />
              )}
            </Stack>
          </Grid>

          <Grid item sm={12} md={12} lg={12} xl={12}>
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={saveUserMutation.isLoading}
              loading={saveUserMutation.isLoading}
              sx={{ width: '120px' }}
            >
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}
